import { useContext, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function Login() {
  const navigate = useNavigate();
  const { style } = useStyle();
  const { refreshUser, isLoading } = useContext(UserContext);

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    email: "",
    password: "",
  });

  const panelBackground = style.color1 || "rgb(39, 55, 77)";
  const borderColor = style.color2 || "rgb(82, 109, 130)";
  const mutedText = style.color3 || "rgb(157, 178, 191)";
  const primaryText = style.color4 || "rgb(221, 230, 237)";
  const accentGreen = style.accentGreen || "rgb(24, 146, 80)";
  const inputBackground = "#0b111a";

  const emailIsValid = useMemo(() => {
    if (!data.email) {
      return true;
    }

    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      data.email.trim()
    );
  }, [data.email]);

  const canSubmit = Boolean(
    data.email.trim() &&
      emailIsValid &&
      data.password &&
      !submitting &&
      !isLoading
  );

  const setField = (field, value) => {
    setData((previousData) => ({
      ...previousData,
      [field]: value,
    }));
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const loginUser = async (event) => {
    event.preventDefault();

    const email = data.email.trim();
    const password = data.password;

    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!emailIsValid) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!password) {
      toast.error("Please enter your password.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.post("/login", {
        email,
        password,
      });

      if (response.data?.error) {
        toast.error(response.data.error);
        return;
      }

      const token = response.data?.token;

      if (!token) {
        throw new Error(
          "The login response did not include a token."
        );
      }

      localStorage.setItem("token", token);

      const authenticatedUser = await refreshUser(token);

      if (!authenticatedUser) {
        throw new Error(
          "Your account information could not be loaded."
        );
      }

      toast.success("Signed in successfully.");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Login failed:", error);

      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to sign in."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sharedInputSx = {
    width: "100%",

    "& .MuiOutlinedInput-root": {
      minHeight: 50,
      color: primaryText,
      backgroundColor: inputBackground,
      borderRadius: "10px",
      transition:
        "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",

      "& fieldset": {
        borderColor: "rgba(255,255,255,0.11)",
      },

      "&:hover": {
        backgroundColor: "#0d1520",

        "& fieldset": {
          borderColor: "rgba(255,255,255,0.20)",
        },
      },

      "&.Mui-focused": {
        backgroundColor: "#0d1520",
        boxShadow: "0 0 0 3px rgba(24,146,80,0.14)",

        "& fieldset": {
          borderColor: accentGreen,
          borderWidth: "1px",
        },
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef6c72",
      },

      "&.Mui-error.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(239,108,114,0.12)",
      },
    },

    "& .MuiInputLabel-root": {
      color: mutedText,
      fontSize: "0.94rem",

      "&.Mui-focused": {
        color: primaryText,
      },

      "&.Mui-error": {
        color: "#ef8a8f",
      },
    },

    "& .MuiInputBase-input": {
      color: primaryText,
      fontSize: "0.95rem",
    },

    "& .MuiInputAdornment-root": {
      color: mutedText,
    },

    "& .MuiFormHelperText-root": {
      mx: 0.25,
      mt: 0.4,
      color: mutedText,
      fontSize: "0.76rem",
      lineHeight: 1.25,
    },

    "& input:-webkit-autofill": {
      WebkitBoxShadow:
        `0 0 0 100px ${inputBackground} inset !important`,
      WebkitTextFillColor: `${primaryText} !important`,
      caretColor: `${primaryText} !important`,
    },
  };

  const primaryButtonSx = {
    minHeight: 48,
    borderRadius: "9px",
    textTransform: "none",
    fontSize: "0.96rem",
    fontWeight: 850,
    color: "#ffffff",
    backgroundColor: accentGreen,
    boxShadow: "0 9px 22px rgba(0,0,0,0.24)",
    transition:
      "transform 160ms ease, filter 160ms ease, box-shadow 160ms ease",

    "&:hover": {
      backgroundColor: accentGreen,
      filter: "brightness(1.08)",
      transform: "translateY(-1px)",
      boxShadow: "0 12px 26px rgba(0,0,0,0.30)",
    },

    "&.Mui-disabled": {
      color: "rgba(255,255,255,0.55)",
      backgroundColor: "rgba(24,146,80,0.40)",
      boxShadow: "none",
    },
  };

  return (
    <>
      <Seo
        title="Login | Parish Audio"
        description="Sign in to your Parish Audio account."
        canonical="https://parishaudio.com/login"
        robots="noindex,follow"
      />

      <Box
        component="main"
        sx={{
          width: "100%",
          minHeight: "calc(100vh - 130px)",
          px: { xs: 1.25, sm: 2 },
          py: { xs: 1.75, sm: 3 },
          display: "flex",
          justifyContent: "center",
          alignItems: { xs: "flex-start", sm: "center" },
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 468,
            overflow: "hidden",
            borderRadius: "16px",
            backgroundColor: panelBackground,
            backgroundImage: "none",
            border: `1px solid ${borderColor}`,
            boxShadow:
              "0 28px 70px rgba(0,0,0,0.48), inset 0 1px 0 rgba(255,255,255,0.035)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 2.2, sm: 3 },
              pt: { xs: 2.35, sm: 2.8 },
              pb: { xs: 1.8, sm: 2.15 },
              backgroundColor: "#111a26",
              borderBottom:
                "1px solid rgba(255,255,255,0.075)",
            }}
          >

            <Stack
              spacing={1.15}
              alignItems="center"
              textAlign="center"
              sx={{
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "12px",
                  color: "#ffffff",
                  backgroundColor: accentGreen,
                  border: "1px solid rgba(255,255,255,0.13)",
                  boxShadow:
                    "0 10px 24px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.12)",
                }}
              >
                <LoginRoundedIcon />
              </Box>

              <Box>
                <Typography
                  component="h1"
                  sx={{
                    color: primaryText,
                    fontSize: {
                      xs: "1.48rem",
                      sm: "1.7rem",
                    },
                    fontWeight: 900,
                    lineHeight: 1.12,
                    letterSpacing: "-0.025em",
                  }}
                >
                  Sign in to Parish Audio
                </Typography>

                <Typography
                  sx={{
                    mt: 0.65,
                    color: mutedText,
                    fontSize: "0.86rem",
                    lineHeight: 1.45,
                  }}
                >
                  Sign in to continue.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box
            component="form"
            onSubmit={loginUser}
            noValidate
            sx={{
              px: { xs: 2.2, sm: 3 },
              pt: { xs: 1.85, sm: 2.2 },
              pb: { xs: 2.1, sm: 2.65 },
            }}
          >
            <Stack spacing={1.2}>
              <TextField
                label="Email Address"
                type="email"
                autoComplete="email"
                autoFocus
                fullWidth
                disabled={submitting}
                value={data.email}
                onChange={(event) =>
                  setField("email", event.target.value)
                }
                error={Boolean(data.email) && !emailIsValid}
                helperText={
                  Boolean(data.email) && !emailIsValid
                    ? "Enter a valid email address."
                    : undefined
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={sharedInputSx}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                fullWidth
                disabled={submitting}
                value={data.password}
                onChange={(event) =>
                  setField("password", event.target.value)
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label={
                          showPassword
                            ? "Hide password"
                            : "Show password"
                        }
                        onClick={() =>
                          setShowPassword(
                            (currentValue) => !currentValue
                          )
                        }
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{
                          color: mutedText,
                          "&:hover": {
                            color: primaryText,
                            backgroundColor:
                              "rgba(255,255,255,0.06)",
                          },
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOffRoundedIcon fontSize="small" />
                        ) : (
                          <VisibilityRoundedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={sharedInputSx}
              />

              <Box sx={{ textAlign: "right", mt: -0.15 }}>
                <Typography
                  component={RouterLink}
                  to="/forgot-password"
                  sx={{
                    color: primaryText,
                    fontSize: "0.8rem",
                    fontWeight: 800,
                    textDecoration: "none",

                    "&:hover": {
                      color: "#ffffff",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Forgot password?
                </Typography>
              </Box>

              <Button
                type="submit"
                fullWidth
                disabled={!canSubmit}
                endIcon={
                  submitting ? null : (
                    <LoginRoundedIcon fontSize="small" />
                  )
                }
                sx={primaryButtonSx}
              >
                {submitting ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <CircularProgress
                      size={17}
                      thickness={5}
                      sx={{ color: "#ffffff" }}
                    />
                    <span>Signing in...</span>
                  </Stack>
                ) : (
                  "Sign In"
                )}
              </Button>

              <Divider
                sx={{
                  my: 0.5,
                  borderColor: "rgba(255,255,255,0.075)",
                }}
              />

              <Stack spacing={0.75} alignItems="center">
                <Typography
                  sx={{
                    color: mutedText,
                    fontSize: "0.84rem",
                  }}
                >
                  Don&apos;t have an account?
                </Typography>

                <Button
                  component={RouterLink}
                  to="/register"
                  fullWidth
                  startIcon={
                    <PersonAddAltRoundedIcon fontSize="small" />
                  }
                  sx={{
                    minHeight: 44,
                    borderRadius: "9px",
                    textTransform: "none",
                    fontWeight: 800,
                    color: primaryText,
                    backgroundColor:
                      "rgba(4,7,12,0.30)",
                    border:
                      "1px solid rgba(255,255,255,0.085)",

                    "&:hover": {
                      color: "#ffffff",
                      backgroundColor:
                        "rgba(4,7,12,0.48)",
                      borderColor:
                        "rgba(255,255,255,0.15)",
                    },
                  }}
                >
                  Create Account
                </Button>
              </Stack>

              <Button
                component={RouterLink}
                to="/terms"
                sx={{
                  alignSelf: "center",
                  minWidth: 0,
                  mt: 0.25,
                  p: 0,
                  textTransform: "none",
                  color: primaryText,
                  fontSize: "0.76rem",
                  fontWeight: 800,

                  "&:hover": {
                    color: "#ffffff",
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Terms and Conditions
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </>
  );
}


// import { useState, useContext, useMemo } from "react";
// import axios from "axios";
// import { toast } from "react-hot-toast";
// import { useNavigate, Link } from "react-router-dom";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import TermsModal from "../Terms/TermsModal";
// import Terms from "../Terms/Terms";
// import Seo from "../../components/Seo";

// import Button from "@mui/material/Button";
// import Box from "@mui/material/Box";
// import Paper from "@mui/material/Paper";
// import Stack from "@mui/material/Stack";
// import Typography from "@mui/material/Typography";
// import Divider from "@mui/material/Divider";
// import TextField from "@mui/material/TextField";
// import FormControl from "@mui/material/FormControl";
// import InputLabel from "@mui/material/InputLabel";
// import OutlinedInput from "@mui/material/OutlinedInput";
// import InputAdornment from "@mui/material/InputAdornment";
// import IconButton from "@mui/material/IconButton";
// import CircularProgress from "@mui/material/CircularProgress";

// import Visibility from "@mui/icons-material/Visibility";
// import VisibilityOff from "@mui/icons-material/VisibilityOff";

// export default function Login() {
//   const navigate = useNavigate();
//   const { style } = useStyle();
//   const { setUser, setUpdateFlag, isLoading } = useContext(UserContext);

//   const [isModalOpen, setModalOpen] = useState(false);
//   const [showPassword, setShowPassword] = useState(false);
//   const [submitting, setSubmitting] = useState(false);

//   const [data, setData] = useState({
//     email: "",
//     password: "",
//   });

//   const openModal = () => setModalOpen(true);
//   const closeModal = () => setModalOpen(false);

//   const handleMouseDownPassword = (event) => {
//     event.preventDefault();
//   };

//   const emailIsValid = useMemo(() => {
//     if (!data.email) return true;
//     return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim());
//   }, [data.email]);

//   const canSubmit =
//     data.email.trim() &&
//     emailIsValid &&
//     data.password &&
//     !submitting &&
//     !isLoading;

//   const navToRegister = () => {
//     navigate("/register");
//   };

//   const setField = (field, value) => {
//     setData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const primaryButtonBg = "rgba(120, 150, 190, 0.14)";
//   const primaryButtonBorder = "rgba(140, 170, 210, 0.28)";
//   const primaryButtonHover = "rgba(120, 150, 190, 0.22)";

//   const sharedInputSx = {
//     "& .MuiInputBase-root": {
//       color: style.color4,
//       backgroundColor: style.darkerPanel || "rgba(6, 9, 14, 0.55)",
//       boxShadow: "none",
//       borderRadius: "10px",
//     },
//     "& .MuiOutlinedInput-root": {
//       "& fieldset": {
//         borderColor: "rgba(255,255,255,0.10)",
//         borderWidth: "1px",
//       },
//       "&:hover fieldset": {
//         borderColor: "rgba(255,255,255,0.16)",
//       },
//       "&.Mui-focused fieldset": {
//         borderColor: style.color3,
//         borderWidth: "1px",
//       },
//     },
//     "& .MuiInputLabel-root": {
//       color: style.color3,
//     },
//     "& .MuiInputLabel-root.Mui-focused": {
//       color: style.color4,
//     },
//     "& .MuiInputBase-input": {
//       color: style.color4,
//       fontSize: "0.95rem",
//     },
//     "& .MuiFormHelperText-root": {
//       marginLeft: 0,
//       marginTop: "4px",
//       minHeight: "20px",
//     },
//     "& input:-webkit-autofill": {
//       WebkitBoxShadow: `0 0 0 100px ${
//         style.darkerPanel || "rgba(6, 9, 14, 0.55)"
//       } inset !important`,
//       WebkitTextFillColor: `${style.color4} !important`,
//       caretColor: `${style.color4} !important`,
//     },
//   };

//   const loginUser = async (e) => {
//     e.preventDefault();

//     const email = data.email.trim();
//     const password = data.password;

//     if (!email) {
//       toast.error("Please enter your email address.");
//       return;
//     }

//     if (!emailIsValid) {
//       toast.error("Please enter a valid email address.");
//       return;
//     }

//     if (!password) {
//       toast.error("Please enter your password.");
//       return;
//     }

//     try {
//       setSubmitting(true);

//       const response = await axios.post("/login", {
//         email,
//         password,
//       });

//       if (response.data?.error) {
//         toast.error(response.data.error);
//         return;
//       }

//       const userData = response.data;

//       localStorage.setItem("token", userData.token);
//       await setUser(userData);
//       await setUpdateFlag();

//       toast.success("Login successful!");
//       navigate("/");
//     } catch (error) {
//       console.error(error);
//       toast.error(
//         error?.response?.data?.error || "An unexpected error occurred while logging in."
//       );
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <>
//       <Seo
//         title="Login | Parish Audio"
//         description="Login to your Parish Audio account."
//         canonical="https://parishaudio.com/login"
//         robots="noindex,follow"
//       />

//       <Box
//         sx={{
//           minHeight: "100%",
//           px: 2,
//           py: { xs: 2, md: 3 },
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "flex-start",
//         }}
//       >
//         <Paper
//           elevation={0}
//           sx={{
//             width: "100%",
//             maxWidth: 500,
//             overflow: "hidden",
//             borderRadius: "10px",
//             backgroundColor: style.color1,
//             border: `1px solid ${style.color2}`,
//             boxShadow: "none",
//           }}
//         >
//           <Box
//             sx={{
//               px: { xs: 2.25, md: 3 },
//               pt: { xs: 2.5, md: 3 },
//               pb: 1.5,
//               borderBottom: `1px solid rgba(255,255,255,0.08)`,
//               backgroundColor: style.darkPanel || "rgba(9, 12, 18, 0.45)",
//             }}
//           >
//             <Stack spacing={1.25} alignItems="center" textAlign="center">
//               <Typography
//                 variant="h5"
//                 sx={{
//                   color: style.color4,
//                   fontWeight: 700,
//                   letterSpacing: "-0.02em",
//                 }}
//               >
//                 Login
//               </Typography>
//             </Stack>
//           </Box>

//           <Box
//             component="form"
//             onSubmit={loginUser}
//             sx={{
//               px: { xs: 2.25, md: 3 },
//               py: { xs: 2.25, md: 3 },
//             }}
//           >
//             <Stack spacing={1.25}>
//               <TextField
//                 label="Email Address"
//                 type="email"
//                 autoComplete="email"
//                 fullWidth
//                 size="small"
//                 value={data.email}
//                 onChange={(e) => setField("email", e.target.value)}
//                 error={!!data.email && !emailIsValid}
//                 helperText={
//                   !!data.email && !emailIsValid ? "Enter a valid email address." : " "
//                 }
//                 sx={sharedInputSx}
//               />

//               <FormControl fullWidth size="small" sx={sharedInputSx}>
//                 <InputLabel htmlFor="login-password">Password</InputLabel>
//                 <OutlinedInput
//                   id="login-password"
//                   type={showPassword ? "text" : "password"}
//                   value={data.password}
//                   autoComplete="current-password"
//                   onChange={(e) => setField("password", e.target.value)}
//                   endAdornment={
//                     <InputAdornment position="end">
//                       <IconButton
//                         aria-label="toggle password visibility"
//                         onClick={() => setShowPassword((show) => !show)}
//                         onMouseDown={handleMouseDownPassword}
//                         edge="end"
//                         sx={{ color: style.color3 }}
//                       >
//                         {showPassword ? (
//                           <VisibilityOff fontSize="small" />
//                         ) : (
//                           <Visibility fontSize="small" />
//                         )}
//                       </IconButton>
//                     </InputAdornment>
//                   }
//                   label="Password"
//                 />
//               </FormControl>

//               <Box
//                 sx={{
//                   mt: -0.25,
//                   textAlign: "center",
//                 }}
//               >
//                 <Link
//                   to="/forgot-password"
//                   style={{
//                     fontSize: "0.88rem",
//                     fontWeight: 700,
//                     color: style.color4,
//                     textDecoration: "none",
//                   }}
//                 >
//                   Forgot Password?
//                 </Link>
//               </Box>

//               <Button
//                 type="submit"
//                 disabled={!canSubmit}
//                 sx={{
//                   color: style.color4,
//                   fontWeight: 700,
//                   margin: "auto",
//                   width: "100%",
//                   backgroundColor: primaryButtonBg,
//                   borderWidth: "1px",
//                   borderColor: primaryButtonBorder,
//                   borderStyle: "solid",
//                   textTransform: "none",
//                   "&:hover": {
//                     backgroundColor: primaryButtonHover,
//                     borderColor: "rgba(160, 190, 230, 0.38)",
//                   },
//                   "&.Mui-disabled": {
//                     color: "rgba(255,255,255,0.45)",
//                     background: "rgba(255,255,255,0.08)",
//                     borderColor: "rgba(255,255,255,0.08)",
//                   },
//                 }}
//               >
//                 {submitting ? (
//                   <Stack direction="row" spacing={1} alignItems="center">
//                     <CircularProgress size={16} sx={{ color: style.color4 }} />
//                     <span>Logging in...</span>
//                   </Stack>
//                 ) : (
//                   "Login"
//                 )}
//               </Button>

//               <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 0.5 }} />

//               <Stack spacing={0.75} alignItems="center">
//                 <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                   Not yet a member?
//                 </Typography>

//                 <Button
//                   onClick={navToRegister}
//                   sx={{
//                     px: 2.25,
//                     py: 0.9,
//                     textTransform: "none",
//                     fontWeight: 700,
//                     fontSize: "0.92rem",
//                     color: style.color4,
//                     backgroundColor: style.darkPanel || "rgba(9, 12, 18, 0.45)",
//                     border: "1px solid rgba(255,255,255,0.08)",
//                     "&:hover": {
//                       backgroundColor: style.darkerPanel || "rgba(6, 9, 14, 0.55)",
//                     },
//                   }}
//                 >
//                   Create Account
//                 </Button>
//               </Stack>

//               <Box sx={{ pt: 0.5, textAlign: "center" }}>
//                 <Typography
//                   sx={{
//                     color: style.color3,
//                     fontSize: "0.84rem",
//                     lineHeight: 1.5,
//                   }}
//                 >
//                   By using this website, you agree to our terms and conditions.
//                 </Typography>

//                 <Button
//                   href="/terms"
//                   sx={{
//                     mt: 0.15,
//                     minWidth: 0,
//                     p: 0.25,
//                     textTransform: "none",
//                     fontWeight: 700,
//                     fontSize: "0.84rem",
//                     color: style.color4,
//                     "&:hover": {
//                       backgroundColor: "transparent",
//                       color: style.color3,
//                     },
//                   }}
//                 >
//                   View terms and conditions
//                 </Button>
//               </Box>
//             </Stack>
//           </Box>
//         </Paper>
//       </Box>

//     </>
//   );
// }
// import { useState, useContext, useMemo } from "react";
// import axios from "axios";
// import { toast } from "react-hot-toast";
// import { useNavigate, Link } from "react-router-dom";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import TermsModal from "../Terms/TermsModal";
// import Terms from "../Terms/Terms";
// import Seo from "../../components/Seo";

// import Button from "@mui/material/Button";
// import Box from "@mui/material/Box";
// import Paper from "@mui/material/Paper";
// import Stack from "@mui/material/Stack";
// import Typography from "@mui/material/Typography";
// import Divider from "@mui/material/Divider";
// import TextField from "@mui/material/TextField";
// import FormControl from "@mui/material/FormControl";
// import InputLabel from "@mui/material/InputLabel";
// import OutlinedInput from "@mui/material/OutlinedInput";
// import InputAdornment from "@mui/material/InputAdornment";
// import IconButton from "@mui/material/IconButton";
// import CircularProgress from "@mui/material/CircularProgress";

// import Visibility from "@mui/icons-material/Visibility";
// import VisibilityOff from "@mui/icons-material/VisibilityOff";

// export default function Login() {
//   const navigate = useNavigate();
//   const { style } = useStyle();
//   const { setUser, setUpdateFlag, isLoading } = useContext(UserContext);

//   const [isModalOpen, setModalOpen] = useState(false);
//   const [showPassword, setShowPassword] = useState(false);
//   const [submitting, setSubmitting] = useState(false);

//   const [data, setData] = useState({
//     email: "",
//     password: "",
//   });

//   const openModal = () => setModalOpen(true);
//   const closeModal = () => setModalOpen(false);

//   const handleMouseDownPassword = (event) => {
//     event.preventDefault();
//   };

//   const emailIsValid = useMemo(() => {
//     if (!data.email) return true;
//     return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim());
//   }, [data.email]);

//   const canSubmit =
//     data.email.trim() &&
//     emailIsValid &&
//     data.password &&
//     !submitting &&
//     !isLoading;

//   const navToRegister = () => {
//     navigate("/register");
//   };

//   const setField = (field, value) => {
//     setData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const sharedInputSx = {
//     "& .MuiInputBase-root": {
//       color: style.color4,
//       backgroundColor: style.color2,
//       boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
//     },
//     "& .MuiOutlinedInput-root": {
//       "& fieldset": {
//         borderColor: style.color3,
//         borderWidth: "1.25px",
//       },
//       "&:hover fieldset": {
//         borderColor: style.color4,
//       },
//       "&.Mui-focused fieldset": {
//         borderColor: style.color4,
//         borderWidth: "1.5px",
//       },
//     },
//     "& .MuiInputLabel-root": {
//       color: style.color3,
//     },
//     "& .MuiInputLabel-root.Mui-focused": {
//       color: style.color4,
//     },
//     "& .MuiInputBase-input": {
//       color: style.color4,
//       fontSize: "0.95rem",
//     },
//     "& .MuiFormHelperText-root": {
//       marginLeft: 0,
//       marginTop: "4px",
//       minHeight: "20px",
//     },
//     "& input:-webkit-autofill": {
//       WebkitBoxShadow: `0 0 0 100px ${style.color2} inset !important`,
//       WebkitTextFillColor: `${style.color4} !important`,
//       caretColor: `${style.color4} !important`,
//     },
//   };

//   const loginUser = async (e) => {
//     e.preventDefault();

//     const email = data.email.trim();
//     const password = data.password;

//     if (!email) {
//       toast.error("Please enter your email address.");
//       return;
//     }

//     if (!emailIsValid) {
//       toast.error("Please enter a valid email address.");
//       return;
//     }

//     if (!password) {
//       toast.error("Please enter your password.");
//       return;
//     }

//     try {
//       setSubmitting(true);

//       const response = await axios.post("/login", {
//         email,
//         password,
//       });

//       if (response.data?.error) {
//         toast.error(response.data.error);
//         return;
//       }

//       const userData = response.data;

//       localStorage.setItem("token", userData.token);
//       await setUser(userData);
//       await setUpdateFlag();

//       toast.success("Login successful!");
//       navigate("/");
//     } catch (error) {
//       console.error(error);
//       toast.error(
//         error?.response?.data?.error || "An unexpected error occurred while logging in."
//       );
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <>
//       <Seo
//         title="Login | Parish Audio"
//         description="Login to your Parish Audio account."
//         canonical="https://parishaudio.com/login"
//         robots="noindex,follow"
//       />

//       <Box
//         sx={{
//           minHeight: "100%",
//           px: 2,
//           py: { xs: 2, md: 3 },
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "flex-start",
//         }}
//       >
//         <Paper
//           elevation={0}
//           sx={{
//             width: "100%",
//             maxWidth: 500,
//             overflow: "hidden",
//             borderRadius: "10px",
//             background: `linear-gradient(180deg, ${style.color1} 0%, rgba(39,55,77,0.96) 100%)`,
//             border: `1px solid ${style.color2}`,
//             boxShadow: "0 20px 50px rgba(0,0,0,0.32)",
//           }}
//         >
//           <Box
//             sx={{
//               px: { xs: 2.25, md: 3 },
//               pt: { xs: 2.5, md: 3 },
//               pb: 1.5,
//               borderBottom: `1px solid rgba(255,255,255,0.08)`,
//               background:
//                 "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))",
//             }}
//           >
//             <Stack spacing={1.25} alignItems="center" textAlign="center">
//               <Typography
//                 variant="h5"
//                 sx={{
//                   color: style.color4,
//                   fontWeight: 700,
//                   letterSpacing: "-0.02em",
//                 }}
//               >
//                 Login
//               </Typography>
//             </Stack>
//           </Box>

//           <Box
//             component="form"
//             onSubmit={loginUser}
//             sx={{
//               px: { xs: 2.25, md: 3 },
//               py: { xs: 2.25, md: 3 },
//             }}
//           >
//             <Stack spacing={1.25}>
//               <TextField
//                 label="Email Address"
//                 type="email"
//                 autoComplete="email"
//                 fullWidth
//                 size="small"
//                 value={data.email}
//                 onChange={(e) => setField("email", e.target.value)}
//                 error={!!data.email && !emailIsValid}
//                 helperText={
//                   !!data.email && !emailIsValid ? "Enter a valid email address." : " "
//                 }
//                 sx={sharedInputSx}
//               />

//               <FormControl fullWidth size="small" sx={sharedInputSx}>
//                 <InputLabel htmlFor="login-password">Password</InputLabel>
//                 <OutlinedInput
//                   id="login-password"
//                   type={showPassword ? "text" : "password"}
//                   value={data.password}
//                   autoComplete="current-password"
//                   onChange={(e) => setField("password", e.target.value)}
//                   endAdornment={
//                     <InputAdornment position="end">
//                       <IconButton
//                         aria-label="toggle password visibility"
//                         onClick={() => setShowPassword((show) => !show)}
//                         onMouseDown={handleMouseDownPassword}
//                         edge="end"
//                         sx={{ color: style.color3 }}
//                       >
//                         {showPassword ? (
//                           <VisibilityOff fontSize="small" />
//                         ) : (
//                           <Visibility fontSize="small" />
//                         )}
//                       </IconButton>
//                     </InputAdornment>
//                   }
//                   label="Password"
//                 />
//               </FormControl>

//               <Box
//                 sx={{
//                   mt: -0.25,
//                   textAlign: "center",
//                 }}
//               >
//                 <Link
//                   to="/forgot-password"
//                   style={{
//                     fontSize: "0.88rem",
//                     fontWeight: 700,
//                     color: style.color4,
//                     textDecoration: "none",
//                   }}
//                 >
//                   Forgot Password?
//                 </Link>
//               </Box>

//               <Button
//                 type="submit"
//                 disabled={!canSubmit}
//                 sx={{
//                   color: style.color4,
//                   fontWeight: 700,
//                   margin: "auto",
//                   width: "100%",
//                   backgroundColor: style.color1,
//                   borderWidth: "1px",
//                   borderColor: style.color3,
//                   filter: "brightness(1.1)",
//                   borderStyle: "solid",
//                   textTransform: "none",
//                   "&:hover": {
//                     backgroundColor: style.color2,
//                   },
//                   "&.Mui-disabled": {
//                     color: "rgba(255,255,255,0.45)",
//                     background: "rgba(255,255,255,0.08)",
//                     borderColor: "rgba(255,255,255,0.08)",
//                   },
//                 }}
//               >
//                 {submitting ? (
//                   <Stack direction="row" spacing={1} alignItems="center">
//                     <CircularProgress size={16} sx={{ color: style.color4 }} />
//                     <span>Logging in...</span>
//                   </Stack>
//                 ) : (
//                   "Login"
//                 )}
//               </Button>

//               <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 0.5 }} />

//               <Stack spacing={0.75} alignItems="center">
//                 <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
//                   Not yet a member?
//                 </Typography>

//                 <Button
//                   onClick={navToRegister}
//                   sx={{
//                     px: 2.25,
//                     py: 0.9,
//                     textTransform: "none",
//                     fontWeight: 700,
//                     fontSize: "0.92rem",
//                     color: style.color4,
//                     backgroundColor: "rgba(255,255,255,0.05)",
//                     border: "1px solid rgba(255,255,255,0.08)",
//                     "&:hover": {
//                       backgroundColor: "rgba(255,255,255,0.10)",
//                     },
//                   }}
//                 >
//                   Create Account
//                 </Button>
//               </Stack>

//               <Box sx={{ pt: 0.5, textAlign: "center" }}>
//                 <Typography
//                   sx={{
//                     color: style.color3,
//                     fontSize: "0.84rem",
//                     lineHeight: 1.5,
//                   }}
//                 >
//                   By using this website, you agree to our terms and conditions.
//                 </Typography>

//                 <Button
//                   onClick={openModal}
//                   sx={{
//                     mt: 0.15,
//                     minWidth: 0,
//                     p: 0.25,
//                     textTransform: "none",
//                     fontWeight: 700,
//                     fontSize: "0.84rem",
//                     color: style.color4,
//                     "&:hover": {
//                       backgroundColor: "transparent",
//                       color: style.color3,
//                     },
//                   }}
//                 >
//                   View terms and conditions
//                 </Button>
//               </Box>
//             </Stack>
//           </Box>
//         </Paper>
//       </Box>

//       <TermsModal isOpen={isModalOpen} close={closeModal}>
//         <Terms />
//       </TermsModal>
//     </>
//   );
// }

// import {useState, useContext, useEffect} from "react";
// import axios from 'axios'
// import {toast} from 'react-hot-toast'
// import {useNavigate, Link} from 'react-router-dom'
// import{UserContext} from '../../context/userContext/userContext';
// import { useStyle } from "../../context/styleContext/styleContext";
// import ReactGA from 'react-ga4';
// import TermsModal from "../Terms/TermsModal"
// import Terms from "../Terms/Terms";
// import Button from '@mui/material/Button';
// import Visibility from '@mui/icons-material/Visibility';
// import VisibilityOff from '@mui/icons-material/VisibilityOff';
// import { Checkbox, TextField, FormCOntrol, InputLabel, OutlinedInput, InputAdornment, FormControl, IconButton, FormGroup, FormControlLabel } from "@mui/material";
// import Seo from "../../components/Seo";

// export default function Login(){
//     const navigate = useNavigate()
//     const [isModalOpen, setModalOpen] = useState(false);
//     const openModal = () => setModalOpen(true);
//     const closeModal = () => setModalOpen(false);
//     const { style, setStyle } = useStyle();
//     const [showPassword, setShowPassword] = useState(false);
//   const [showPassword2, setShowPassword2] = useState(false);
//   const [showConfirm, setShowConfirm] = useState(false);
//   const handleClickShowPassword = () => setShowPassword((show) => !show);
//   const handleMouseDownPassword = (event) => {
//     event.preventDefault();
//   };
    

//     const[data,setData] = useState({
//         email: '',
//         password: ''
//     })

//     const navToRegister = () => {
//         navigate('/register')
//       }

//     const { setUser } = useContext(UserContext);
//     const { isLoading,  } = useContext(UserContext);
//     const { setUpdateFlag } = useContext(UserContext);


//     const wait = (n) => new Promise((resolve) => setTimeout(resolve, n));

//     // 
    
//     const loginUser = async (e) => {
//       e.preventDefault();
  
//       const { email, password } = data;
  
//       try {
//           const { data } = await axios.post('/login', {
//               email,
//               password
//           });
  
//           if (data.error) {
//               toast.error(data.error);
//           } else {
//               toast.success("Login successful, welcome!");
//               localStorage.setItem('token', data.token);
//               await setUser(data);
//               //window.location.href = `/`;
//               navigate('/');
              
//           }
//       } catch (error) {
//           // Handle Axios error response
//           if (error.response && error.response.data && error.response.data.error) {
//               toast.error(error.response.data.error);
//           } else {
//               toast.error("An unexpected error occurred while logging in.");
//           }
//       }
//   };

//     return(
//         <><>
//             <Seo
//       title="Login | Parish Audio"
//       description="Login to your Parish Audio account."
//       canonical="https://parishaudio.com/login"
//       robots="noindex,follow"
//     />

//         <div style={{display:'flex', flexDirection:'column', width:'100%', marginBottom:'25px'}}>

// <div style={{display:'flex', flexDirection:'column', width:'90%', maxWidth:'600px', margin:'auto', color:style.color4, textAlign:'center', marginTop:'25px', backgroundColor:style.color1, borderRadius:'10px', borderStyle:'solid', borderColor:style.color1, borderWidth:'5px'}}>
//     <div style={{width:'90%', margin:'auto'}}>

    
//         <div style={{display:'flex', flex:1, textAlign:'center', marginTop:'25px'}}>
//             <h1 style={{flex:1, textAlign:'center', margin:'auto', marginBottom:'20px', marginTop:'10px', fontSize:'2.0em'}}>
//                 Login to your account
//             </h1>
           
//         </div>
//         <div style={{maxWidth:'400px', margin:'auto'}}>
//         <div style={{display:'flex', flex:1, textAlign:'center', minWidth:'300px', margin:'auto', marginBottom:'10px'}}>
//             <h4 style={{flex:1, textAlign:'left', margin:'auto', color:style.color1}}>
//                 <div style={{marginBottom:'5px'}}>
//                 <TextField
//                 id="outlined-basic"
//                 label="Email"
//                 variant="outlined"
//                 value= {data.firstName}
//                 onChange={(e) => setData({...data, email: e.target.value})}
//                 size='small'
//                 fullWidth
//                 sx={{
//                     backgroundColor: style.color2,
//                     borderRadius:'4px',
//                     '& .MuiInputBase-input': {
//                     color: style.color4,  // Change the font color here
//                     fontSize: '16pt',  // Change the font size here
//                     boxShadow:5,
                                                    
//                     },
//                     '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                         borderColor: style.color1, // Change the border color if needed
//                         borderWidth:'2px'
//                     },
//                     '&:hover fieldset': {
//                         borderColor: style.color1, // Change the border color on hover if needed\
//                         borderWidth:'3px',                                                               
                 
                        
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color1, // Change the border color when focused if needed
//                         borderWidth:'3px'
                        
//                     },
//                     '&.Mui-disabled': {
//                         '& fieldset': {
//                         borderColor: style.color1, // Change the border color when disabled
//                         },
//                     },
//                     },
//                     '& .MuiInputLabel-root': {
//                     color: style.color3, // Change the label color here
//                     fontSize:'14pt',
//                     },
//                     '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3, // Change the label color when focused here                                 
//                     },
//                     '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color2, // Change the label color when disabled
//                     },
//                     '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                         color: style.color3, // Change the input font color when disabled // so it works in safari
//                         WebkitTextFillColor: style.color4, // so it works on chrome
//                         fontSize:'14pt'
//                     },
//                     },
//                     '& .MuiOutlinedInput-input': { color: style.color4,
//                     '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset',
//                         WebkitTextFillColor: style.color4, color:style.color4
//                      }, }, 
//                 }}
//                 />
                

//                 </div>
//                 </h4>
//         </div>

//         <div style={{display:'flex', flex:1, marginBottom:'10px', width:'100%', backgroundColor:style.color1}}>     

//             <FormControl sx={{
//                   backgroundColor: style.color2,
//                   flex: 1,
//                   width:'100%',
//                   size: 'small',
//                   margin: 'auto',
//                   boxShadow:5,
                  

//                   '& .MuiInputBase-input': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                     size: 'small',
//                   },
//                   '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                       borderColor: style.color1,
//                       borderWidth: '2px'
//                     },
//                     '&:hover fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-focused fieldset': {
//                       borderColor: style.color2,
//                       borderWidth: '3px',
//                     },
//                     '&.Mui-disabled': {
//                       '& fieldset': {
//                         borderColor: style.color1,
//                       },
//                     },
//                   },
//                   '& .MuiInputLabel-root': {
//                     color: style.color3,
//                     fontSize: '14pt',
//                   },
//                   '& .MuiInputLabel-root.Mui-focused': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputLabel-root.Mui-disabled': {
//                     color: style.color3,
//                   },
//                   '& .MuiInputBase-root.Mui-disabled': {
//                     '& input': {
//                       color: style.color3,
//                       WebkitTextFillColor: style.color1,
//                       fontSize: '14pt'
//                     },
//                   },
//                   '& .MuiOutlinedInput-input': {
//                     color: style.color4,
//                     '& input:-webkit-autofill': {
//                     WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                     WebkitTextFillColor: `${style.color4} !important`,
//                     color: `${style.color4} !important`,
//                     caretColor: `${style.color4} !important`,
//                 },
//                   },
//                 }} variant="outlined">
//                   <InputLabel size="small" htmlFor="outlined-adornment-password">Password</InputLabel>
//                   <OutlinedInput
//                     id="outlined-adornment-password"
//                     type={showPassword ? 'text' : 'password'}
//                     value={data.password}
//                     size='small'
//                     onChange={(e) => setData({...data, password: e.target.value})}
//                     sx={{
//                         '& input:-webkit-autofill': {
//                         WebkitBoxShadow: '0 0 0 100px rgb(82, 109, 130) inset !important',
//                         WebkitTextFillColor: `${style.color4} !important`,
//                         color: `${style.color4} !important`,
//                         caretColor: `${style.color4} !important`,
                        
//                     },
//                     }}
//                     endAdornment={
//                       <InputAdornment position="end">
//                         <IconButton
//                           aria-label="toggle password visibility"
//                           onClick={handleClickShowPassword}
//                           onMouseDown={handleMouseDownPassword}
//                           edge="end"
//                           sx={{ color: style.color1 }}
//                         >
//                           {showPassword ? <VisibilityOff /> : <Visibility />}
//                         </IconButton>
//                       </InputAdornment>
//                     }
//                     label="Password"
//                   />
  
//                  </FormControl>
                          
//                         </div>
//                         <div >
//                           <Link to='/forgot-password' style={{fontSize:'.9em', marginTop:'10px', fontWeight:'bold', textAlign:'center', color:style.color4, margin:'auto', alignContent:'center'}}>
//                               Forgot Password?
//                         </Link>
//                           </div>
       

        

//         {(data.email && data.password ) &&(
//         <div style={{marginTop:'25px', marginBottom:'50px', display:'flex', flex:1}}>
//             <Button onClick={loginUser}  sx={{ my: 2, color: style.color4, display: 'block', backgroundColor: style.color1, filter: 'brightness(1.1)', boxShadow:5,fontSize: '14pt', width: '75%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 }, }}>Login</Button>
//         </div>
//         )}
//         {(!data.email || !data.password ) &&(
//         <div style={{marginTop:'25px', marginBottom:'50px', display:'flex', flex:1}}>
//             <Button disabled onClick={loginUser} sx={{ my: 2, color:style.color2, display:'block', backgroundColor:style.color1, fontSize:'14pt', width:'75%', margin:'auto', marginTop:'0px', borderColor:style.color2, borderWidth:'1px', borderStyle:'solid', '&:hover': {backgroundColor: style.color4 }, }}><span style={{color:style.color3}}>Login</span></Button>
//         </div>
//         )} 

//         <div style={{marginBottom:'30px'}}>
//             <h4>Not yet a member? Join for free!</h4>

//             <Button size='small' onClick={navToRegister} sx={{ my: 2, color: style.color3, display: 'block', margin: 'auto', boxShadow:5,backgroundColor: style.color1, filter: 'brightness(1.1)', fontSize: '12pt', width: '75%', minWidth: '150px', margin: 'auto', marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 } }}>
//                <strong>Create Account</strong>
//             </Button>


//             </div>
//             <div style={{ flex:1, borderBottomLeftRadius:'15px', color:style.color5, marginBottom:'10px'}}>                 
//                     By using this website, you agree to our 
//                     <div>
//                         <Button size='small' sx={{ my: 2, color: style.color3, display: 'block', margin: 'auto', backgroundColor: style.color1, fontSize: '12pt', width: '100%', minWidth: '250px', margin: 'auto', marginTop: '0px', borderColor: style.color1, borderWidth: '2px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color1, color:style.color4 } }} onClick={setModalOpen.bind(null, true)}><strong>terms and conditions</strong></Button>
//                     </div>
//                     </div>

//                     <div id='modal-root'>
//                     <TermsModal isOpen={isModalOpen} close={closeModal}>
//                         <Terms />
//                     </TermsModal>
//                     </div>
            
//                 </div>                




//     </div>

//     </div>

// </div>
// <div id='modal-root'>
// </div>


//         </>
//         </>         

//     )

    
// }

